import { useId } from 'react';
import { Loader2 } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import type { GalleryLinkBlock, GalleryLinkGate } from './shareGate';
import type { PublicLink } from './usePublicLink';

export const GALLERY_TOGGLE_COPY = {
  onTitle: 'PlanOtour is On',
  offTitle: 'Turn On PlanOtour',
  on: 'Anyone with the link can view these photos. Turn off to disable the link.',
  off: 'Creates a public link anyone can view. No sign-in needed.',
  // The wording the View PhotoPlan control used (DV-3480) for a link left on
  // a photoset that was hidden afterwards.
  revokeOnly:
    'This photoset is hidden from the client, so its link shows “not available” until the photoset is made visible again. Turn the link off here to retire it for good.'
} as const;

/** Why the switch cannot be turned on, one line per block. */
export const GALLERY_BLOCK_COPY: Record<GalleryLinkBlock, string> = {
  'no-order': "This photoset isn't linked to an order yet, so it can't be shared publicly.",
  'order-pending': "Checking the order's status…",
  'order-unknown': "Couldn't check the order's status. Reload the page to try again.",
  'order-canceled': "This order was canceled, so its photos can't be shared publicly.",
  'order-incomplete': "This order couldn't be completed, so its photos can't be shared publicly.",
  'not-delivered': 'The public link becomes available once the order is delivered.',
  hidden: 'The public link becomes available once this photoset is visible to the client.'
};

const description = (on: boolean, gate: GalleryLinkGate): string => {
  if (on) return gate.hiddenFromClient ? GALLERY_TOGGLE_COPY.revokeOnly : GALLERY_TOGGLE_COPY.on;
  if (gate.blockedBy !== null) return GALLERY_BLOCK_COPY[gate.blockedBy];
  return GALLERY_TOGGLE_COPY.off;
};

interface GalleryLinkToggleProps {
  link: PublicLink;
  gate: GalleryLinkGate;
  /** Draws its own bordered card (the quick menu). The Share modal's tab
   *  passes false and frames it together with the link below. */
  framed?: boolean;
  className?: string;
}

/**
 * The PlanOtour link's on/off row, the same in the header's quick menu
 * and the Share modal's tab. Only the switch toggles the link: the row is not
 * its label, so a click on the title or the description does nothing (they
 * still name and describe the switch for assistive technology). Turning the
 * link on is disabled while `gate.blockedBy` says asset-service would refuse
 * it; turning a live link off never is.
 */
const GalleryLinkToggle = ({ link, gate, framed = true, className }: GalleryLinkToggleProps) => {
  const id = useId();
  const on = link.url !== null;
  const disabled = link.busy || (!on && gate.blockedBy !== null);

  return (
    <div
      className={cn(
        'flex items-center gap-3 p-3',
        framed && 'rounded-lg border border-border bg-background',
        className
      )}
    >
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span id={`${id}-title`} className="text-sm font-semibold text-foreground">
          {on ? GALLERY_TOGGLE_COPY.onTitle : GALLERY_TOGGLE_COPY.offTitle}
        </span>
        <span id={`${id}-description`} className="text-xs text-muted-foreground">
          {description(on, gate)}
        </span>
      </span>
      {/* The switch stays mounted while a request is in flight (disabled,
          spinner beside it): unmounting it would drop keyboard focus. */}
      <span className="flex shrink-0 items-center gap-2">
        {link.busy && (
          <Loader2
            className="h-4 w-4 animate-spin text-muted-foreground"
            aria-hidden
            data-testid="gallery-link-busy"
          />
        )}
        <Switch
          id={`${id}-switch`}
          checked={on}
          onCheckedChange={link.setOn}
          disabled={disabled}
          aria-busy={link.busy}
          aria-labelledby={`${id}-title`}
          aria-describedby={`${id}-description`}
        />
      </span>
    </div>
  );
};

export default GalleryLinkToggle;
